import BugsnagPerformance from '@bugsnag/browser-performance'

/**
 * Métricas de performance do checkout no Bugsnag Performance.
 *
 * Os spans customizados são "first class" (aparecem agregados no painel) e,
 * por padrão, não viram o contexto atual, para não adotarem como filhos as
 * requisições de outras partes da página. Qualquer falha aqui é engolida:
 * medir não pode quebrar o checkout.
 */

// Analytics, pixels e fingerprint de antifraude: não medem o checkout e
// seguram o "carregamento" da página enquanto enviam eventos
const IGNORED_DOMAINS = [
    'posthog.com',
    'google-analytics.com',
    'analytics.google.com',
    'googletagmanager.com',
    'googleadservices.com',
    'googlesyndication.com',
    'doubleclick.net',
    'google.com',
    'google.com.br',
    'facebook.com',
    'facebook.net',
    'tiktok.com',
    'tiktokw.us',
    'kwai.net',
    'kwai.com',
    'pinterest.com',
    'taboola.com',
    'klaviyo.com',
    'clarity.ms',
    'online-metrix.net',
    'bugsnag.com',
]

const IGNORED_URL = new RegExp(
    `^https?://([^/?#]+\\.)?(${IGNORED_DOMAINS.map((domain) => domain.replace(/\./g, '\\.')).join('|')})(?=[/:?#]|$)`,
    'i'
)

// Polling do Pix: até 90 requisições por página, sem valor como métrica
const POLLING_URL = /\/checkout\/checkPayment\//

export const SETTLE_IGNORE_URLS = [IGNORED_URL, POLLING_URL]

/**
 * Troca por ":id" os segmentos que parecem identificadores (hash, CEP, id
 * numérico, e-mail), para agrupar as chamadas e não mandar dados do cliente.
 * Versões curtas como "v1" e "apiv3" ficam como estão.
 */
function maskIdSegment(segment) {
    const looksLikeId = (segment.length >= 6 && /\d/.test(segment))
        || /^\d{4,}$/.test(segment)
        || segment.length >= 32
        || /@|%40/.test(segment)

    return looksLikeId ? ':id' : segment
}

/**
 * Remove query string e fragmento (levam card_number, e-mail, ?ch=...) e,
 * se pedido, mascara os identificadores do caminho.
 */
export function scrubUrl(rawUrl, { maskIds = false, base } = {}) {
    try {
        const url = new URL(rawUrl, base || window.location.href)
        const path = maskIds ? url.pathname.split('/').map(maskIdSegment).join('/') : url.pathname

        return url.origin + path
    } catch (e) {
        return null
    }
}

/**
 * networkRequestCallback do Bugsnag. Vale para fetch/XHR e também para os
 * recursos da página (script, img, css), que chegam aqui com `type` igual ao
 * initiatorType do Resource Timing.
 */
export function networkRequestCallback(info) {
    const isApiCall = info.type === 'fetch' || info.type === 'xmlhttprequest'

    if (!isApiCall) {
        // Nunca devolver null para recurso: no SDK 2.15 isso interrompe o lote
        // inteiro de entradas do PerformanceObserver
        const url = info.url && scrubUrl(info.url)
        return url ? { ...info, url } : info
    }

    if (!info.url || IGNORED_URL.test(info.url) || POLLING_URL.test(info.url)) {
        return null
    }

    const url = scrubUrl(info.url, { maskIds: true })
    return url ? { ...info, url } : null
}

/**
 * Ambiente pelo host em que a página roda: previews da Vercel respondem em
 * *.vercel.app e os domínios próprios (pay.unicopag.com.br e os white-label)
 * são produção. Decidir em runtime mantém o valor certo se um preview for
 * promovido para produção sem novo build.
 */
export function releaseStageFor(hostname) {
    if (/^(localhost|127\.0\.0\.1|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+)$/.test(hostname)) {
        return 'development'
    }

    return /\.vercel\.app$/.test(hostname) ? 'preview' : 'production'
}

/**
 * Resolve a URL para o padrão da rota do Nuxt (/:page, /customer/:transaction),
 * senão cada produto ou pedido vira um item separado no painel.
 */
export function createRouteResolver(getRouter) {
    return (url) => {
        try {
            const router = getRouter()
            const matched = router ? router.match(url.pathname).matched : []
            if (matched.length > 0) {
                return matched[matched.length - 1].path || '/'
            }
        } catch (e) {
            // cai no padrão abaixo
        }

        return '/(sem rota)'
    }
}

const endedSpans = typeof WeakSet === 'function' ? new WeakSet() : null

function isValidAttribute(value) {
    return typeof value === 'boolean'
        || (typeof value === 'number' && isFinite(value))
        || (typeof value === 'string' && value !== '')
}

export function setSpanAttributes(span, attributes) {
    if (!span || !attributes) return

    Object.keys(attributes).forEach((name) => {
        const value = attributes[name]
        if (!isValidAttribute(value)) return

        try {
            span.setAttribute(name, value)
        } catch (e) {
            // atributo inválido não pode derrubar o fluxo
        }
    })
}

export function startSpan(name, options = {}, attributes = null) {
    try {
        const span = BugsnagPerformance.startSpan(name, {
            isFirstClass: true,
            makeCurrentContext: false,
            ...options,
        })
        setSpanAttributes(span, attributes)

        return span
    } catch (e) {
        return null
    }
}

/** Encerra o span uma única vez (chamadas repetidas são ignoradas). */
export function endSpan(span, endTime) {
    if (!span || (endedSpans && endedSpans.has(span))) return

    if (endedSpans) endedSpans.add(span)

    try {
        span.end(endTime)
    } catch (e) {
        // idem
    }
}

// A aba escondida durante o carregamento invalida a medição: o navegador
// desprioriza a página e o tempo deixa de refletir a espera do cliente
let pageWasHidden = false

if (typeof document !== 'undefined') {
    pageWasHidden = document.visibilityState === 'hidden'
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') pageWasHidden = true
    })
}

function wasPageHidden() {
    if (pageWasHidden) return true

    try {
        // Chrome 115+ guarda o histórico de visibilidade desde a navegação,
        // cobrindo o intervalo antes deste módulo carregar
        return performance.getEntriesByType('visibility-state').some((entry) => entry.name === 'hidden')
    } catch (e) {
        return false
    }
}

function isPreviewMode() {
    try {
        return new URLSearchParams(window.location.search).get('preview') === 'true'
    } catch (e) {
        return false
    }
}

function afterNextPaint(callback) {
    if (typeof requestAnimationFrame !== 'function') {
        setTimeout(callback, 0)
        return
    }

    requestAnimationFrame(() => setTimeout(callback, 0))
}

function connectionAttributes() {
    const connection = typeof navigator !== 'undefined' ? navigator.connection : null

    return {
        'network.effective_type': connection && connection.effectiveType,
    }
}

const timelines = {}

/**
 * Linha do tempo de uma página, do início da navegação até o conteúdo pintado.
 *
 * `mark(trecho)` fecha um trecho desde a marca anterior (a primeira começa no
 * tempo 0 da navegação) e `finish(trecho, atributos)` fecha o último trecho e o
 * total depois da próxima pintura. Os spans só são criados no `finish`, já com
 * início e fim: se a página falhar no meio, ou a aba for escondida, nada é
 * enviado e nenhum span fica aberto.
 */
export function pageTimeline(name) {
    if (!timelines[name]) {
        timelines[name] = createTimeline(name)
    }

    return timelines[name]
}

function createTimeline(name) {
    const segments = []
    let lastMark = 0
    let finished = false

    function addSegment(segmentName, time) {
        if (segments.some((segment) => segment.name === segmentName)) return false

        segments.push({ name: segmentName, start: lastMark, end: time })
        lastMark = time
        return true
    }

    return {
        mark(segmentName) {
            if (finished) return
            addSegment(segmentName, performance.now())
        },

        finish(segmentName, attributes) {
            if (finished || isPreviewMode()) return
            finished = true

            afterNextPaint(() => {
                if (wasPageHidden()) return

                const end = performance.now()
                if (segmentName) addSegment(segmentName, end)

                const total = startSpan(name, { startTime: 0 }, { ...attributes, ...connectionAttributes() })
                if (!total) return

                segments.forEach((segment) => {
                    const span = startSpan(`${name}: ${segment.name}`, {
                        startTime: segment.start,
                        parentContext: total,
                    })
                    endSpan(span, segment.end)
                })

                endSpan(total, end)
            })
        },
    }
}
